import { type FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { _notNil } from '@/littledash';
import Header from '@/components/UI/Header';
import Loading from '@/components/Loading';
import { NoDataMessage } from '@/components/NoDataCard';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ApiService } from '@/support/ApiService';
import { errorToast, successToast } from '@/helpers';
import InVivoError from '@/model/InVivoError.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { TeamThemeService } from '@/support/TeamThemeService';
import { useModalAction } from '@/utils/modal';
import type { Color } from '@/model/Common.model';
import FormActions from '../FormActions';
import ThemeCard from './ThemeCard';
import StudiesReadAllCard from './StudiesReadAllCard';
import { type TeamConfigFormValues, type TeamConfigResponse, buildPatchPayload } from './TeamConfig.utils';
import styles from './TeamConfig.module.scss';

const DEFAULT_PRIMARY = '#1B2A4A';
const DEFAULT_ON_PRIMARY = '#FFFFFF';

/**
 * Admin console page for managing tenant-wide configuration settings.
 * Provides controls for sidebar theme colours and studies_read_all permission.
 */
export const TeamConfig: FC = () => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/team/config',
    invokeOnInit: true,
  });

  if (loading) {
    return <Loading txt="Loading configuration" />;
  }

  if (!response?.body) {
    return (
      <div className="pa4" data-test-component="TeamConfig" data-test-element="no-data">
        <NoDataMessage
          title="Unable to Load Configuration"
          text="There was a problem loading tenant configuration. Please refresh the page or try again later."
        />
      </div>
    );
  }

  return <TeamConfigForm data={response.body} />;
};

interface TeamConfigFormProps {
  data: TeamConfigResponse;
}

const TeamConfigForm: FC<TeamConfigFormProps> = ({ data }) => {
  const { openModal } = useModalAction();

  const defaultValues: TeamConfigFormValues = {
    theme: {
      active: data.theme.active,
      value: {
        primary: data.theme.value?.primary ?? DEFAULT_PRIMARY,
        on_primary: data.theme.value?.on_primary ?? DEFAULT_ON_PRIMARY,
      },
    },
    studies_read_all: {
      active: data.studies_read_all.active,
    },
  };

  const formMethods = useForm<TeamConfigFormValues>({ defaultValues, mode: 'onChange' });
  const {
    handleSubmit,
    reset,
    formState: { dirtyFields, isValid, isSubmitting },
  } = formMethods;

  const doSave = async (formData: TeamConfigFormValues) => {
    try {
      const body = buildPatchPayload(formData, dirtyFields);
      await ApiService.call({
        endpoint: 'PATCH /api/v1/team/config',
        body,
        options: { onError: { toast: false, throw: true, capture: false } },
      });
    } catch (cause) {
      errorToast('Error saving tenant configuration, please try again.');
      ExceptionHandler.captureException(
        new InVivoError('Could not save tenant configuration', { cause, slug: 'team-config-save' })
      );
      return;
    }

    if (_notNil(dirtyFields.theme)) {
      if (formData.theme.active) {
        TeamThemeService.apply({
          primary: formData.theme.value.primary as Color,
          on_primary: formData.theme.value.on_primary as Color,
        });
      } else {
        TeamThemeService.clear();
      }
    }

    successToast('Tenant configuration saved');
    reset(formData);
  };

  const onSubmit = async (formData: TeamConfigFormValues) => {
    if ('studies_read_all' in dirtyFields) {
      openModal('CONFIRM_STUDIES_READ_ALL_CHANGE', {
        onClick: async () => {
          await doSave(formData);
        },
      });
      return;
    }
    await doSave(formData);
  };

  const pendingCount = Object.keys(dirtyFields).length;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} data-test-component="TeamConfig" data-test-element="form">
        <div className="pb3">
          <Header
            mainHeaderText="Tenant Config"
            asideComponent={() => (
              <FormActions
                pendingCount={pendingCount}
                isValid={isValid}
                isSubmitting={isSubmitting}
                onDiscard={() => reset()}
                onSave={handleSubmit(onSubmit)}
              />
            )}
          />
          <p className="f6 dark-gray mt1 mb4">Manage tenant-wide configuration settings.</p>

          <div className={styles.cardStack}>
            <ThemeCard />
            <StudiesReadAllCard />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default TeamConfig;
